import IntegrationProvider from "@/model/interface/IntegrationProvider";

const LAUNCHED_PROVIDER_KEYS = ["podio", "salesforce", "hubspot"];

const PROVIDER_CATALOG: IntegrationProvider[] = [
  {
    key: "salesforce",
    name: "Salesforce",
    description: "Bidirectional contact and opportunity sync with call and SMS activity logging.",
    category: "CRM",
    auth: "oauth",
    available: true,
    accent: "#00A1E0",
  },
  {
    key: "hubspot",
    name: "HubSpot",
    description: "Two-way contact and deal sync with call and SMS engagement logging.",
    category: "CRM",
    auth: "oauth",
    available: true,
    accent: "#FF7A59",
  },
  {
    key: "podio",
    name: "Podio",
    description: "Map your Podio apps and fields, with call and SMS logging.",
    category: "CRM",
    auth: "oauth",
    available: true,
    accent: "#5A4FCF",
  },
  {
    key: "ghl",
    name: "GoHighLevel",
    description: "Sync contacts across your sub-account and trigger workflows on new leads.",
    category: "Marketing CRM",
    auth: "oauth",
    available: true,
    accent: "#2A3A4A",
  },
  {
    key: "carrot",
    name: "Carrot CRM",
    description: "Lead sync with call and text logging for Carrot CRM, formerly InvestorFuse.",
    category: "REI CRM",
    auth: "apikey",
    available: true,
    accent: "#F26B3A",
  },
  {
    key: "reisift",
    name: "REISift",
    description: "Sync leads with REISift, with property/lead linking.",
    category: "REI CRM",
    auth: "apikey",
    available: true,
    accent: "#3B82F6",
  },
  {
    key: "dealmachine",
    name: "DealMachine",
    description: "Bring your driving for dollars leads and lists into Retreevo as they are added.",
    category: "Lead source",
    auth: "apikey",
    available: false,
    accent: "#2563EB",
  },
  {
    key: "airtable",
    name: "Airtable",
    description: "Map an Airtable base and table for contacts and activity.",
    category: "Database",
    auth: "oauth",
    available: false,
    accent: "#F59E0B",
  },
  {
    key: "tape",
    name: "Tape",
    description: "Podio-style app mapping with call and SMS logging.",
    category: "Database",
    auth: "apikey",
    available: false,
    accent: "#0EA5E9",
  },
  {
    key: "forefront",
    name: "ForeFront",
    description: "Contact sync and activity logging for ForeFront CRM.",
    category: "REI CRM",
    auth: "apikey",
    available: false,
    accent: "#10B981",
  },
  {
    key: "realeflow",
    name: "Realeflow",
    description: "Contact lookup and call/SMS logging for Realeflow.",
    category: "REI CRM",
    auth: "apikey",
    available: false,
    accent: "#EF4444",
  },
  {
    key: "zapier",
    name: "Zapier",
    description: "Connect the tools that only offer Zapier through Retreevo triggers and actions.",
    category: "Automation",
    auth: "apikey",
    available: false,
    accent: "#FF4A00",
  },
];

export const INTEGRATION_PROVIDERS: IntegrationProvider[] = PROVIDER_CATALOG.map((provider) => ({
  ...provider,
  available: provider.available && LAUNCHED_PROVIDER_KEYS.includes(provider.key),
}));

/** CRM providers that support "Sign in with" OAuth login (must have a live OAuth integration). */
export const OAUTH_LOGIN_PROVIDER_KEYS = ["salesforce", "hubspot", "podio", "ghl"] as const;

export function getProvider(service: string | undefined): IntegrationProvider | undefined {
  return INTEGRATION_PROVIDERS.find((p) => p.key === service);
}

export function providerInitials(name: string): string {
  const cleaned = name.replace(/[^a-zA-Z0-9 ]/g, "").trim();
  const parts = cleaned.split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return cleaned.slice(0, 2).toUpperCase();
}
